<script setup lang="ts">
import { computed } from 'vue'
import { ChevronLeft, ChevronRight } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
    pageSize: number
    pageSizeOptions?: number[]
    hasPrev: boolean
    hasNext: boolean
    pageIndex: number
    rangeStart?: number
    rangeEnd?: number
    totalCount?: number | null
}>()

const emit = defineEmits<{
    (e: 'prev'): void
    (e: 'next'): void
    (e: 'update:pageSize', v: number): void
}>()

const { t } = useI18n()

function onPageSizeChange(e: Event) {
    emit('update:pageSize', Number((e.target as HTMLSelectElement).value))
}

const label = computed(() => {
    if (props.rangeStart && props.rangeEnd) {
        if (props.totalCount != null) {
            return t('common.pagination.rangeOf', {
                from: props.rangeStart,
                to: props.rangeEnd,
                total: props.totalCount
            })
        }
        return t('common.pagination.range', {
            from: props.rangeStart,
            to: props.rangeEnd
        })
    }
    return t('common.pagination.page', { n: props.pageIndex + 1 })
})
</script>

<template>
    <div class="flex items-center gap-3 px-4 py-3 border-t border-gray-200 bg-white">
        <div class="ml-auto flex items-center gap-2">
            <span class="text-xs text-gray-500 mr-4">
                {{ label }}
            </span>
            <select
                class="text-xs h-7 px-2 border border-gray-200 rounded-6 bg-white"
                :value="props.pageSize"
                @change="onPageSizeChange"
            >
                <option v-for="o in pageSizeOptions ?? [25, 50, 100, 200]" :key="o" :value="o">{{ o }}</option>
            </select>
            <span class="text-xs text-gray-500">{{ t('common.pagination.perPage') }}</span>
            <button
                type="button"
                :disabled="!hasPrev"
                class="w-7 h-7 rounded-6 border border-gray-200 bg-white text-gray-600 hover:border-teal hover:text-teal disabled:opacity-25 disabled:cursor-default flex items-center justify-center"
                @click="emit('prev')"
            >
                <ChevronLeft class="w-3.5 h-3.5" />
            </button>
            <button
                type="button"
                :disabled="!hasNext"
                class="w-7 h-7 rounded-6 border border-gray-200 bg-white text-gray-600 hover:border-teal hover:text-teal disabled:opacity-25 disabled:cursor-default flex items-center justify-center"
                @click="emit('next')"
            >
                <ChevronRight class="w-3.5 h-3.5" />
            </button>
        </div>
    </div>
</template>
